<template>
  <v-container id="dashboard-owner-view" fluid tag="section">
    <v-row align="center" class="mb-2">
      <v-col cols="12" md="5">
        <h2 class="mb-1">Dashboard Owner</h2>
        <div class="text-caption text--secondary">Ringkasan kinerja rute {{ selectedDirectionLabel }} dan prioritas tindakan</div>
      </v-col>
      <v-col cols="12" sm="4" md="2"><v-select v-model="filter.arah" :items="directionOptions" label="Rute" dense outlined hide-details @change="fetchSummary" /></v-col>
      <v-col cols="12" sm="4" md="2"><v-text-field v-model="filter.dari" type="date" label="Dari" dense outlined hide-details /></v-col>
      <v-col cols="12" sm="4" md="2"><v-text-field v-model="filter.sampai" type="date" label="Sampai" dense outlined hide-details /></v-col>
      <v-col cols="12" sm="4" md="1"><v-btn block color="primary" :loading="loading" @click="fetchSummary"><v-icon>mdi-refresh</v-icon></v-btn></v-col>
    </v-row>

    <v-alert v-if="error" type="error" dense dismissible @input="error = ''">{{ error }}</v-alert>
    <v-progress-linear v-if="loading" color="primary" indeterminate rounded height="5" class="mb-4" />

    <v-card outlined class="total-revenue-card mb-2">
      <v-card-text class="d-flex align-center">
        <v-avatar color="primary" size="56" class="mr-4"><v-icon dark>mdi-finance</v-icon></v-avatar>
        <div>
          <div class="text-caption text-uppercase">Omzet Total</div>
          <div class="total-revenue-value">{{ formatCurrency(summary.omset_total_semua_unit) }}</div>
          <div class="text-caption">JKT–PTK + PTK–JKT + Declaring</div>
        </div>
      </v-card-text>
    </v-card>

    <v-row dense>
      <v-col v-for="card in unitSummaryCards" :key="card.key" cols="12" sm="6">
        <v-card
          outlined
          class="metric-card unit-summary-card"
          :class="{ 'metric-card--clickable': card.direction }"
          :style="{ borderLeftColor: card.borderColor }"
          @click="card.direction && openPiutangDetail(card.direction)"
        >
          <v-card-text class="d-flex align-center">
            <v-avatar :color="card.color" size="46" class="mr-4"><v-icon dark>{{ card.icon }}</v-icon></v-avatar>
            <div class="min-width-0">
              <div class="text-caption text-uppercase text--secondary">{{ card.title }}</div>
              <div class="metric-value">{{ formatCurrency(card.value) }}</div>
              <div class="text-caption text--secondary">{{ card.caption }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div class="section-heading section-heading--compact">
      <div><v-icon color="green" class="mr-2">mdi-cash-multiple</v-icon><strong>Arus Kas & Posisi Keuangan {{ selectedDirectionLabel }}</strong></div>
    </div>
    <v-row dense>
      <v-col v-for="card in statCards" :key="card.key" cols="12" sm="6" lg="3">
        <v-card outlined class="metric-card">
          <v-card-text class="d-flex align-center">
            <v-avatar :color="card.color" size="46" class="mr-4"><v-icon dark>{{ card.icon }}</v-icon></v-avatar>
            <div class="min-width-0">
              <div class="text-caption text-uppercase text--secondary">{{ card.title }}</div>
              <div class="metric-value">{{ formatCurrency(card.value) }}</div>
              <div class="text-caption text--secondary text-truncate">{{ card.caption }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showPiutangDetail" max-width="520" scrollable>
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon color="deep-orange" class="mr-2">mdi-receipt-text-clock-outline</v-icon>
          Rincian Piutang per Jenis Nota
          <v-spacer />
          <v-btn icon small @click="showPiutangDetail = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <div v-if="summary.rincian_piutang && summary.rincian_piutang.length">
            <div v-for="item in summary.rincian_piutang" :key="item.kode_nota" class="piutang-row">
              <div class="d-flex align-center">
                <v-chip small :color="item.kode_nota === 'ND' ? 'blue' : item.kode_nota === 'P' ? 'deep-orange' : 'grey'" dark class="mr-3" style="min-width:42px;justify-content:center">{{ item.kode_nota }}</v-chip>
                <div>
                  <div class="font-weight-medium">{{ kodeNotaLabel(item.kode_nota) }}</div>
                  <div class="text-caption text--secondary">{{ item.jumlah }} nota belum lunas</div>
                </div>
              </div>
              <div class="text-right">
                <div class="font-weight-bold">{{ formatCurrency(item.total) }}</div>
              </div>
            </div>
            <v-divider class="my-3" />
            <div class="text-subtitle-2 mb-2">Piutang per Rute</div>
            <div v-for="route in summary.rincian_piutang_per_arah" :key="route.arah" class="route-row">
              <div>
                <strong>{{ route.label }}</strong>
                <div class="text-caption text--secondary">{{ route.jumlah }} nota belum lunas</div>
              </div>
              <strong>{{ formatCurrency(route.total) }}</strong>
            </div>
            <v-divider class="my-3" />
            <div class="d-flex justify-space-between">
              <strong>Total Piutang</strong>
              <strong class="deep-orange--text">{{ formatCurrency(summary.total_piutang) }}</strong>
            </div>
          </div>
          <div v-else class="text-center text--secondary py-4">Tidak ada piutang pada periode ini.</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-row dense>
      <v-col cols="12" lg="8">
        <v-card outlined height="100%">
          <v-card-title class="text-subtitle-1">Tren Keuangan Bulanan</v-card-title>
          <v-card-text>
            <div v-if="summary.trend_bulanan.length" class="chart">
              <div v-for="row in summary.trend_bulanan" :key="row.periode" class="chart-column">
                <div class="bars">
                  <div class="bar primary" :style="{ height: barHeight(row.omset) }" :title="`Omzet ${formatCurrency(row.omset)}`" />
                  <div class="bar success" :style="{ height: barHeight(row.kas_masuk) }" :title="`Kas masuk ${formatCurrency(row.kas_masuk)}`" />
                  <div class="bar error" :style="{ height: barHeight(row.kas_keluar) }" :title="`Kas keluar ${formatCurrency(row.kas_keluar)}`" />
                </div>
                <span>{{ row.label }}</span>
              </div>
            </div>
            <div v-else class="empty-state">Belum ada data pada periode ini.</div>
            <div class="legend mt-3"><span><i class="primary" />Omzet</span><span><i class="success" />Kas masuk</span><span><i class="error" />Kas keluar</span></div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="4">
        <v-card outlined height="100%">
          <v-card-title class="text-subtitle-1">Umur Piutang</v-card-title>
          <v-card-text>
            <div v-for="item in summary.aging_piutang" :key="item.label" class="mb-4">
              <div class="d-flex justify-space-between text-body-2"><span>{{ item.label }} ({{ item.jumlah }})</span><b>{{ formatCurrency(item.nilai) }}</b></div>
              <v-progress-linear :value="agingPercent(item.nilai)" color="deep-orange" rounded height="8" class="mt-1" />
            </div>
            <div v-if="!summary.aging_piutang.length" class="empty-state">Tidak ada piutang.</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" md="7">
        <v-card outlined>
          <v-card-title class="text-subtitle-1">10 Customer dengan Omzet Terbesar</v-card-title>
          <v-data-table :headers="customerHeaders" :items="summary.top_customer" dense :items-per-page="10" hide-default-footer>
            <template #item.omset="{ item }">{{ formatCurrency(item.omset) }}</template>
            <template #item.piutang="{ item }"><span :class="{ 'deep-orange--text': item.piutang > 0 }">{{ formatCurrency(item.piutang) }}</span></template>
            <template #no-data><span class="text--secondary">Belum ada data customer.</span></template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col cols="12" md="5">
        <v-card outlined>
          <v-card-title class="text-subtitle-1">Ringkasan Operasional</v-card-title>
          <v-list dense>
            <v-list-item
              v-for="item in operationalCards"
              :key="item.label"
              :to="item.to"
              class="operational-link"
              :title="`Buka ${item.label}`"
            >
              <v-list-item-icon><v-icon :color="item.color">{{ item.icon }}</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title>{{ item.label }}</v-list-item-title></v-list-item-content>
              <v-list-item-action class="operational-value">
                <strong class="text-h6">{{ formatNumber(item.value) }}</strong>
                <v-icon small color="grey">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <div class="section-heading">
      <div><v-icon color="purple" class="mr-2">mdi-briefcase-variant-outline</v-icon><strong>Analitik Declaring</strong></div>
      <span>Profitabilitas, progres pekerjaan, dan risiko SPK</span>
    </div>
    <v-row dense>
      <v-col v-for="card in declaringCards" :key="card.label" cols="6" md="3">
        <v-card outlined class="unit-card" :class="`${card.color}--border`">
          <v-card-text>
            <div class="text-caption text-uppercase text--secondary">{{ card.label }}</div>
            <div class="text-h6 font-weight-bold" :class="`${card.color}--text`">{{ card.currency ? formatCurrency(card.value) : formatNumber(card.value) }}</div>
            <div class="caption text--secondary">{{ card.caption }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" lg="7">
        <v-card outlined height="100%">
          <v-card-title class="text-subtitle-1">Profitabilitas Customer Declaring</v-card-title>
          <v-data-table :headers="declaringCustomerHeaders" :items="summary.declaring.customer" dense :items-per-page="10" hide-default-footer>
            <template #item.tagihan="{ item }">{{ formatCurrency(item.tagihan) }}</template>
            <template #item.realisasi="{ item }">{{ formatCurrency(item.realisasi) }}</template>
            <template #item.laba="{ item }"><strong :class="item.laba >= 0 ? 'success--text' : 'error--text'">{{ formatCurrency(item.laba) }}</strong></template>
            <template #item.margin="{ item }"><v-chip x-small :color="item.margin >= 20 ? 'success' : item.margin >= 0 ? 'warning' : 'error'" dark>{{ item.margin }}%</v-chip></template>
            <template #no-data><span class="text--secondary">Belum ada transaksi Declaring.</span></template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col cols="12" lg="5">
        <v-card outlined height="100%">
          <v-card-title class="text-subtitle-1">Status Pekerjaan</v-card-title>
          <v-card-text>
            <div class="donut-wrap">
              <div class="donut" :style="declaringDonutStyle">
                <div><strong>{{ formatNumber(summary.declaring.jumlah_spk) }}</strong><span>Total SPK</span></div>
              </div>
              <div class="status-list">
                <div><i class="success" /><span>Selesai</span><strong>{{ formatNumber(summary.declaring.spk_selesai) }}</strong></div>
                <div><i class="primary" /><span>Dalam proses</span><strong>{{ formatNumber(summary.declaring.spk_proses) }}</strong></div>
                <div><i class="error" /><span>Terlambat</span><strong>{{ formatNumber(summary.declaring.spk_terlambat) }}</strong></div>
                <div><i class="warning" /><span>Piutang</span><strong>{{ formatCurrency(summary.declaring.piutang) }}</strong></div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-subtitle-1"><v-icon color="error" class="mr-2">mdi-timeline-alert-outline</v-icon>SPK yang Memerlukan Perhatian</v-card-title>
          <v-data-table :headers="declaringRiskHeaders" :items="summary.declaring.risiko" dense :items-per-page="10" hide-default-footer>
            <template #item.rencana_selesai="{ item }">{{ formatDate(item.rencana_selesai) }}</template>
            <template #item.tagihan="{ item }">{{ formatCurrency(item.tagihan) }}</template>
            <template #item.laba="{ item }"><span :class="item.laba >= 0 ? 'success--text' : 'error--text'">{{ formatCurrency(item.laba) }}</span></template>
            <template #item.status="{ item }"><v-chip x-small :color="item.terlambat ? 'error' : item.status === 'selesai' ? 'success' : 'primary'" dark>{{ item.terlambat ? `${item.hari_terlambat} hari terlambat` : item.status }}</v-chip></template>
            <template #item.pelunasan="{ item }"><v-chip x-small outlined :color="item.pelunasan === 'lunas' ? 'success' : 'warning'">{{ readable(item.pelunasan) }}</v-chip></template>
            <template #no-data><span class="text--secondary">Semua pekerjaan dan pelunasan terkendali.</span></template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <div class="section-heading">
      <div><v-icon color="cyan darken-2" class="mr-2">mdi-bank-transfer</v-icon><strong>Kas External</strong></div>
      <span>Pergerakan dana antarunit dan Declaring</span>
    </div>
    <v-row dense>
      <v-col cols="12" md="4">
        <v-card outlined class="cash-card cash-in"><v-card-text><span>Kas Masuk</span><strong>{{ formatCurrency(summary.kas_external.masuk) }}</strong><small>{{ summary.kas_external.transaksi }} transaksi dalam periode</small></v-card-text></v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="cash-card cash-out"><v-card-text><span>Kas Keluar</span><strong>{{ formatCurrency(summary.kas_external.keluar) }}</strong><small>Pergerakan dana keluar</small></v-card-text></v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="cash-card cash-net"><v-card-text><span>Saldo Bersih</span><strong>{{ formatCurrency(summary.kas_external.bersih) }}</strong><small>Masuk dikurangi keluar</small></v-card-text></v-card>
      </v-col>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-subtitle-1">Arus Dana per Arah</v-card-title>
          <v-data-table :headers="externalHeaders" :items="summary.kas_external.per_arah" dense hide-default-footer>
            <template #item.arah="{ item }">{{ directionLabel(item.arah) }}</template>
            <template #item.masuk="{ item }"><span class="success--text">{{ formatCurrency(item.masuk) }}</span></template>
            <template #item.keluar="{ item }"><span class="error--text">{{ formatCurrency(item.keluar) }}</span></template>
            <template #item.bersih="{ item }"><strong :class="item.bersih >= 0 ? 'success--text' : 'error--text'">{{ formatCurrency(item.bersih) }}</strong></template>
            <template #no-data><span class="text--secondary">Belum ada transaksi Kas External.</span></template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-subtitle-1"><v-icon color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>Prioritas Pembayaran</v-card-title>
          <v-data-table :headers="priorityHeaders" :items="summary.prioritas" dense :items-per-page="10" hide-default-footer>
            <template #item.tanggal="{ item }">{{ formatDate(item.tanggal) }}</template>
            <template #item.nilai="{ item }">{{ formatCurrency(item.nilai) }}</template>
            <template #item.status="{ item }"><v-chip x-small :color="item.terlambat ? 'error' : 'warning'" dark>{{ item.status }}</v-chip></template>
            <template #no-data><span class="text--secondary">Tidak ada pembayaran yang perlu diprioritaskan.</span></template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";

const today = new Date();
export default {
  name: "DashboardOwner",
  data() {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const arah = user.lokasi === "pontianak" ? "pontianak_jakarta" : "jakarta_pontianak";
    return {
      loading: false,
      error: "",
      showPiutangDetail: false,
      filter: { arah, dari: `${today.getFullYear()}-01-01`, sampai: today.toISOString().slice(0, 10) },
      directionOptions: [
        { text: "Pontianak → Jakarta", value: "pontianak_jakarta" },
        { text: "Jakarta → Pontianak", value: "jakarta_pontianak" }
      ],
      summary: {
        total_omset: 0, total_piutang: 0, total_hutang: 0, total_giro: 0,
        omset_total_semua_unit: 0, piutang_total_semua_unit: 0,
        ringkasan_unit: {
          jakarta_pontianak: { omset: 0, piutang: 0, jumlah_nota_belum_lunas: 0 },
          pontianak_jakarta: { omset: 0, piutang: 0, jumlah_nota_belum_lunas: 0 },
          declaring: { omset: 0, piutang: 0, jumlah_nota_belum_lunas: 0 }
        },
        kas_masuk: 0, kas_keluar: 0, arus_kas_bersih: 0, rasio_pelunasan: 0,
        jumlah_nota_belum_lunas: 0, jumlah_tagihan_belum_lunas: 0, jumlah_giro_belum_cair: 0,
        trend_bulanan: [], aging_piutang: [], top_customer: [], prioritas: [], operasional: {}, rincian_piutang: [], rincian_piutang_per_arah: []
        , konsolidasi: { pendapatan: 0, pengeluaran: 0, hasil_bersih: 0, total_piutang: 0 }
        , declaring: { jumlah_spk: 0, spk_proses: 0, spk_selesai: 0, spk_terlambat: 0, nilai_tagihan: 0, total_realisasi: 0, laba_bersih: 0, margin: 0, piutang: 0, customer: [], risiko: [] }
        , kas_external: { masuk: 0, keluar: 0, bersih: 0, transaksi: 0, per_arah: [] }
      },
      customerHeaders: [
        { text: "Customer", value: "customer" }, { text: "Nota", value: "jumlah_nota", align: "right" },
        { text: "Omzet", value: "omset", align: "right" }, { text: "Piutang", value: "piutang", align: "right" }
      ],
      priorityHeaders: [
        { text: "Jenis", value: "jenis" }, { text: "Referensi", value: "referensi" },
        { text: "Jatuh Tempo", value: "tanggal" }, { text: "Nilai", value: "nilai", align: "right" }, { text: "Status", value: "status" }
      ],
      declaringCustomerHeaders: [
        { text: "Customer", value: "customer" }, { text: "SPK", value: "jumlah_spk", align: "right" },
        { text: "Tagihan", value: "tagihan", align: "right" }, { text: "Realisasi", value: "realisasi", align: "right" },
        { text: "Laba", value: "laba", align: "right" }, { text: "Margin", value: "margin", align: "center" }
      ],
      declaringRiskHeaders: [
        { text: "No. SPK", value: "no_spk" }, { text: "Customer", value: "customer" }, { text: "Pekerjaan", value: "pekerjaan" },
        { text: "Rencana", value: "rencana_selesai" }, { text: "Tagihan", value: "tagihan", align: "right" },
        { text: "Laba", value: "laba", align: "right" }, { text: "Pekerjaan", value: "status" }, { text: "Pelunasan", value: "pelunasan" }
      ],
      externalHeaders: [
        { text: "Arah", value: "arah" }, { text: "Transaksi", value: "transaksi", align: "center" },
        { text: "Masuk", value: "masuk", align: "right" }, { text: "Keluar", value: "keluar", align: "right" },
        { text: "Bersih", value: "bersih", align: "right" }
      ]
    };
  },
  computed: {
    selectedDirectionLabel() {
      return this.directionLabel(this.filter.arah);
    },
    unitSummaryCards() {
      const units = this.summary.ringkasan_unit || {};
      const jktPtk = units.jakarta_pontianak || {};
      const ptkJkt = units.pontianak_jakarta || {};
      const declaring = units.declaring || {};
      return [
        { key: "omset-jkt-ptk", title: "Omzet JKT–PTK", value: jktPtk.omset, caption: "Jakarta → Pontianak", icon: "mdi-chart-line", color: "primary", borderColor: "#1976d2" },
        { key: "piutang-jkt-ptk", title: "Piutang JKT–PTK", value: jktPtk.piutang, caption: `${this.formatNumber(jktPtk.jumlah_nota_belum_lunas)} nota belum lunas`, icon: "mdi-receipt-text-clock-outline", color: "deep-orange", borderColor: "#ff5722", direction: "jakarta_pontianak" },
        { key: "omset-ptk-jkt", title: "Omzet PTK–JKT", value: ptkJkt.omset, caption: "Pontianak → Jakarta", icon: "mdi-chart-line", color: "cyan darken-1", borderColor: "#00acc1" },
        { key: "piutang-ptk-jkt", title: "Piutang PTK–JKT", value: ptkJkt.piutang, caption: `${this.formatNumber(ptkJkt.jumlah_nota_belum_lunas)} nota belum lunas`, icon: "mdi-receipt-text-clock-outline", color: "orange darken-2", borderColor: "#f57c00", direction: "pontianak_jakarta" },
        { key: "omset-declaring", title: "Omzet Declaring", value: declaring.omset, caption: `${this.formatNumber(declaring.jumlah_spk)} SPK`, icon: "mdi-briefcase-variant-outline", color: "purple", borderColor: "#9c27b0" },
        { key: "piutang-declaring", title: "Piutang Declaring", value: declaring.piutang, caption: `${this.formatNumber(declaring.jumlah_belum_lunas)} SPK belum lunas`, icon: "mdi-receipt-text-clock-outline", color: "warning", borderColor: "#ff9800" }
      ];
    },
    statCards() {
      return [
        { key: "cashflow", title: "Arus Kas Bersih", value: this.summary.arus_kas_bersih, caption: `Masuk ${this.formatCurrency(this.summary.kas_masuk)} · Keluar ${this.formatCurrency(this.summary.kas_keluar)}`, icon: "mdi-swap-vertical-bold", color: this.summary.arus_kas_bersih >= 0 ? "success" : "error" },
        { key: "giro", title: "Giro Belum Cair", value: this.summary.total_giro, caption: `${this.summary.jumlah_giro_belum_cair} giro`, icon: "mdi-bank-outline", color: "teal" }
      ];
    },
    operationalCards() {
      const op = this.summary.operasional || {};
      return [
        { label: "TTB diterima", value: op.ttb, icon: "mdi-clipboard-text-outline", color: "primary", to: "/ttb/input-edit-ttb" },
        { label: "Container dibuat", value: op.container, icon: "mdi-truck-cargo-container", color: "indigo", to: "/container/input-edit-container" },
        { label: "Kapal berangkat", value: op.vessel, icon: "mdi-ferry", color: "teal", to: "/vessel/data-vessel" },
        { label: "Container belum ada kapal", value: op.container_belum_berangkat, icon: "mdi-alert-outline", color: "warning", to: "/container/daftar-container-belum-berangkat" }
      ];
    },
    declaringCards() {
      const d = this.summary.declaring;
      return [
        { label: "Nilai Tagihan", value: d.nilai_tagihan, currency: true, caption: `${d.jumlah_spk} SPK`, color: "purple" },
        { label: "Realisasi Biaya", value: d.total_realisasi, currency: true, caption: "Biaya pekerjaan", color: "deep-orange" },
        { label: "Laba Bersih", value: d.laba_bersih, currency: true, caption: `Margin ${d.margin}%`, color: d.laba_bersih >= 0 ? "success" : "error" },
        { label: "Piutang Declaring", value: d.piutang, currency: true, caption: "Tagihan belum lunas", color: "warning" }
      ];
    },
    declaringDonutStyle() {
      const total = Number(this.summary.declaring.jumlah_spk) || 1;
      const done = Number(this.summary.declaring.spk_selesai) / total * 100;
      return { background: `conic-gradient(#4caf50 0 ${done}%, #1976d2 ${done}% 100%)` };
    },
    chartMax() {
      return Math.max(1, ...this.summary.trend_bulanan.flatMap(row => [row.omset, row.kas_masuk, row.kas_keluar]));
    }
  },
  created() { this.fetchSummary(); },
  methods: {
    async openPiutangDetail(direction) {
      this.filter.arah = direction;
      await this.fetchSummary();
      if (!this.error) this.showPiutangDetail = true;
    },
    async fetchSummary() {
      this.loading = true; this.error = "";
      try {
        const { data } = await axios.get("info/owner-dashboard", { params: this.filter });
        this.summary = { ...this.summary, ...(data || {}) };
        if (data.periode) this.filter = { ...this.filter, ...data.periode };
      } catch (err) {
        this.error = err.response?.data?.message || "Gagal memuat dashboard owner";
      } finally { this.loading = false; }
    },
    formatCurrency(value) { return `Rp ${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(Number(value) || 0)}`; },
    formatNumber(value) { return new Intl.NumberFormat("id-ID").format(Number(value) || 0); },
    formatDate(value) { return value ? new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(`${value}T00:00:00`)) : "-"; },
    barHeight(value) { return `${Math.max(2, (Number(value) || 0) / this.chartMax * 150)}px`; },
    agingPercent(value) { return this.summary.total_piutang ? Number(value) / this.summary.total_piutang * 100 : 0; },
    readable(value) { return String(value || "-").replace(/_/g, " "); },
    directionLabel(value) {
      const option = this.directionOptions.find(item => item.value === value);
      return option ? option.text : this.readable(value);
    },
    kodeNotaLabel(kode) {
      return { ND: "Nota Debit", P: "Piutang", L: "Lainnya" }[kode] || kode;
    }
  }
};
</script>

<style scoped>
.metric-card { min-height: 116px; }
.total-revenue-card { color: white; background: linear-gradient(120deg, #00b8d4, #1976d2); border: none !important; }
.total-revenue-value { font-size: 1.75rem; font-weight: 800; line-height: 1.35; }
.unit-summary-card { border-left-width: 4px !important; }
.metric-card--clickable { cursor: pointer; transition: box-shadow .2s, border-color .2s; }
.metric-card--clickable:hover { box-shadow: 0 2px 8px rgba(0,0,0,.12); border-color: #ff5722 !important; }
.piutang-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid #eee; }
.piutang-row:last-of-type { border-bottom: none; }
.route-row { display: flex; justify-content: space-between; align-items: center; padding: 7px 0; }
.operational-link { cursor: pointer; transition: background-color .2s; }
.operational-link:hover { background: rgba(25, 118, 210, .06); }
.operational-value { flex-direction: row; align-items: center; gap: 6px; }
.metric-value { color: #202124; font-size: 1.25rem; font-weight: 700; line-height: 1.4; white-space: nowrap; }
.min-width-0 { min-width: 0; }
.chart { height: 190px; display: flex; align-items: flex-end; gap: 10px; overflow-x: auto; padding-top: 10px; }
.chart-column { min-width: 58px; flex: 1; text-align: center; font-size: 10px; color: #757575; }
.bars { height: 155px; display: flex; align-items: flex-end; justify-content: center; gap: 3px; border-bottom: 1px solid #ddd; }
.bar { width: 10px; min-height: 2px; border-radius: 3px 3px 0 0; transition: height .25s ease; }
.legend { display: flex; flex-wrap: wrap; gap: 18px; font-size: 12px; color: #666; }
.legend i { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 5px; }
.empty-state { min-height: 100px; display: flex; align-items: center; justify-content: center; color: #888; }
.section-heading { display: flex; justify-content: space-between; align-items: center; margin: 24px 2px 10px; }
.section-heading--compact { margin-top: 14px; }
.section-heading > div { font-size: 18px; }
.section-heading > span { color: #777; font-size: 12px; }
.unit-card { min-height: 100px; border-left-width: 4px !important; }
.purple--border { border-left-color: #9c27b0 !important; }
.deep-orange--border { border-left-color: #ff5722 !important; }
.success--border { border-left-color: #4caf50 !important; }
.error--border { border-left-color: #f44336 !important; }
.warning--border { border-left-color: #ff9800 !important; }
.donut-wrap { display: flex; align-items: center; justify-content: space-around; gap: 28px; min-height: 205px; }
.donut { width: 150px; height: 150px; border-radius: 50%; display: grid; place-items: center; position: relative; }
.donut::after { content: ""; width: 105px; height: 105px; border-radius: 50%; background: white; position: absolute; }
.donut div { z-index: 1; display: flex; flex-direction: column; text-align: center; }
.donut strong { font-size: 25px; }
.donut span { font-size: 11px; color: #777; }
.status-list { flex: 1; }
.status-list div { display: flex; align-items: center; gap: 8px; padding: 7px 0; border-bottom: 1px solid #eee; }
.status-list i { width: 9px; height: 9px; border-radius: 50%; }
.status-list span { flex: 1; font-size: 12px; color: #666; }
.cash-card { min-height: 118px; position: relative; overflow: hidden; }
.cash-card::after { font-family: "Material Design Icons"; position: absolute; right: 15px; bottom: -13px; font-size: 75px; opacity: .08; content: "\F019D"; }
.cash-card .v-card__text { display: flex; flex-direction: column; }
.cash-card span { font-size: 12px; color: #777; text-transform: uppercase; }
.cash-card strong { font-size: 22px; margin: 5px 0; }
.cash-card small { color: #888; }
.cash-in { border-top: 3px solid #4caf50 !important; }
.cash-out { border-top: 3px solid #f44336 !important; }
.cash-net { border-top: 3px solid #0097a7 !important; }
@media (max-width: 800px) {
  .section-heading > span { display: none; }
  .donut-wrap { flex-direction: column; }
}
</style>
